// FileViewerSplitscreenManager.jsx
// Description: This component manages the splitscreen functionality in the file viewer, allowing users to view

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React, { useState, useContext, useEffect, useRef } from "react"; // { useState, c useEffectreateContext, }
import PropTypes from "prop-types";

// MUI imports
import { Grid } from "@mui/material";
import { IconButton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Tooltip } from "@mui/material";

import ImportProgressDialog from "./ImportProgressDialog";

// Icon imports
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

// HSA KIT Components
import Backend from "../../common/utils/Backend";
import FileRendererDataProvider from "./FileRendererDataProvider";

//Context Imports
import { withPersistentStorage } from "../../viewer/contexts/PersistentStorageContext";
import { withTiles } from "../../viewer/contexts/TilesContext";
import { FileViewerContext } from "../FileViewer";

const useStyles = makeStyles(() => ({
  rendererGridRoot: {
    background: "#EBEBEB",
    position: "relative",
    width: "100%",
    height: "100%",
    overflowY: "inherit",
    overflowX: "inherit",
    gridTemplateColumns: " auto;",
  },
  dropfieldRoot: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.514)",
    color: "rgb(255, 255, 255)",
    zIndex: 100,
    pointerEvents: "none",
    border: "dashed white 4px",
    textAlign: "center",
    paddingTop: "calc(50vh - 74px)",
    fontSize: "20px",
  },
  splitscreenGridRoot: {
    flexGrow: 0,
    margin: 0,
    position: "relative",
    background: "#EBEBEB",
    overflow: "hidden",
  },
  activeIndicatorBorder: {
    position: "absolute",
    pointerEvents: "none",
    zIndex: 100,
    height: "100%",
    width: "100%",
    border: "solid #0673C1 4px",
  },
  layoutButtonContainer: {
    position: "absolute",
    top: 5,
    right: 5,
    zIndex: 100,
  },
  layoutButton: {
    color: "#666",
  },
  importProgressContainer: {
    position: "relative",
    width: 400,
    padding: 6,
    boxSizing: "border-box",
  },
  importProgressBar: {
    height: 32,
    borderRadius: 4,
  },
  importProgressText: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFF",
    fontWeight: 700,
    fontSize: 14,
    pointerEvents: "none",
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
  },
}));

function FileViewerSplitscreenManager(props) {
  const FV = useContext(FileViewerContext);
  const classes = useStyles();

  const [showSplitscreenDropzone, setShowSplitscreenDropzone] = useState(false);
  const [lastFileId, setLastFileId] = useState(null);
  const prevActiveRef = useRef(null);
  const restoringRef = useRef(false);
  const restoredIdsRef = useRef(null);
  const restoredActiveRef = useRef(null);
  const hasUrlFileIdRef = useRef(false);

  useEffect(() => {
    try {
      // Prefer URL-provided fileId over any localStorage restoration
      try {
        const params = new URLSearchParams(window.location.search || "");
        hasUrlFileIdRef.current = params.has("fileId");
      } catch (e) {
        hasUrlFileIdRef.current = false;
      }
      const rawIds = localStorage.getItem("lastSplitscreenFileIds");
      const rawActive = localStorage.getItem("lastActiveFileId");
      const parsedIds = rawIds ? JSON.parse(rawIds) : null;
      const loadedIds = Array.isArray(parsedIds)
        ? parsedIds.filter(Boolean)
        : [];
      const loadedActive = rawActive || null;
      // Check current URL fileId (if any)
      let urlFileId = null;
      try {
        const params = new URLSearchParams(window.location.search || "");
        urlFileId = params.get("fileId");
      } catch (e) {
        urlFileId = null;
      }

      // If URL has a fileId and it's NOT part of the saved splitscreen, clear splitscreen and activate URL file.
      if (
        urlFileId &&
        loadedIds.length >= 1 &&
        !loadedIds.includes(urlFileId)
      ) {
        // URL dictates a different single view: set split to just that URL file
        FV.setSplitscreenFileIds([urlFileId]);
        try {
          localStorage.setItem(
            "lastSplitscreenFileIds",
            JSON.stringify([urlFileId])
          );
        } catch (e) {
          /* ignore */
        }
        if (FV.activeFileId !== urlFileId) FV.setActiveFileId(urlFileId);
        setLastFileId(urlFileId);
        return; // Do not restore saved panes since URL dictates a different single view
      }
      // If we have saved panes, restore them even if the URL has a fileId.
      // When the URL has a fileId, we keep it as the active pane if it is part of the saved set.
      if (loadedIds.length >= 1) {
        restoringRef.current = true;
        restoredIdsRef.current = loadedIds;
        restoredActiveRef.current = loadedActive;
        // Apply splitscreen panes from storage
        FV.setSplitscreenFileIds(loadedIds);

        // Determine which pane should be active on restore
        let nextActive = null;
        // If URL specifies a fileId and it's part of the saved panes, prefer that
        if (urlFileId && loadedIds.includes(urlFileId)) {
          nextActive = urlFileId;
        }
        // Otherwise use the previously active pane if available
        if (!nextActive && loadedActive && loadedIds.includes(loadedActive)) {
          nextActive = loadedActive;
        }
        // Fallback to the first pane
        if (!nextActive) nextActive = loadedIds[0];

        if (nextActive) {
          if (FV.activeFileId !== nextActive) FV.setActiveFileId(nextActive);
          setLastFileId(nextActive);
        }
        // Allow state to settle before enabling regular effects
        setTimeout(() => {
          restoringRef.current = false;
          FV.updateDimensions();
        }, 0);
      }
    } catch (e) {
      // ignore restore errors
    }

    const onResize = () => FV.updateDimensions();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // If a parent cleared the splitscreen list during initial mount before activeFileId was set,
  // rehydrate it once an activeFileId is known.
  useEffect(() => {
    if (!FV.activeFileId) return;
    if (FV.splitscreenFileIds.length === 0) {
      let ids = null;
      try {
        const raw = localStorage.getItem("lastSplitscreenFileIds");
        const parsed = raw ? JSON.parse(raw) : null;
        ids = Array.isArray(parsed) ? parsed.filter(Boolean) : null;
      } catch (e) {
        // ignore
      }
      // If navigation specified a fileId in the URL and it's not in saved panes, clear splitscreen
      if (hasUrlFileIdRef.current) {
        let urlFileId = null;
        try {
          const params = new URLSearchParams(window.location.search || "");
          urlFileId = params.get("fileId");
        } catch (e) {
          urlFileId = null;
        }
        if (ids && ids.length > 0 && urlFileId && !ids.includes(urlFileId)) {
          ids = [urlFileId]; // Set splitscreen to the URL fileId
        }
      } else {
        if (!ids || ids.length === 0) ids = restoredIdsRef.current;
        if (!ids || ids.length === 0) ids = [FV.activeFileId];
      }
      if (ids && ids.length) {
        FV.setSplitscreenFileIds(ids);
        try {
          localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(ids));
        } catch (e) {
          // ignore persistence errors
        }
      }
    }
  }, [FV.activeFileId, FV.splitscreenFileIds.length]);

  // Single-view mode behavior: keep splitscreen list in sync with activeFileId, but only as a single entry.
  // This prevents unintended second panes when the user simply selects a file.
  useEffect(() => {
    if (restoringRef.current) return; // avoid clobbering while restoring
    // When there's no active file, ensure no pane is rendered in single mode
    if (FV.activeFileId === null) {
      if (FV.splitscreenFileIds.length !== 0) {
        FV.setSplitscreenFileIds([]);
        localStorage.setItem("lastSplitscreenFileIds", JSON.stringify([]));
      }
      return;
    }
    // Only enforce single-entry behavior when not in a real splitscreen (<=1 panes)
    if (FV.splitscreenFileIds.length <= 1) {
      const existing = FV.splitscreenFileIds[0];
      if (existing !== FV.activeFileId) {
        const next = [FV.activeFileId];
        FV.setSplitscreenFileIds(next);
        localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(next));
      }
    }
  }, [FV.activeFileId]);

  // When active selection is cleared or the splitscreen list becomes empty, reset lastFileId
  useEffect(() => {
    if (FV.activeFileId === null || FV.splitscreenFileIds.length === 0) {
      setLastFileId(null);
    }
  }, [FV.activeFileId, FV.splitscreenFileIds.length]);

  // Ensure we have a valid selected pane tracked whenever we are in real split mode.
  useEffect(() => {
    if (FV.splitscreenFileIds.length > 1) {
      if (!lastFileId || !FV.splitscreenFileIds.includes(lastFileId)) {
        const fallback = FV.activeFileId || FV.splitscreenFileIds[0] || null;
        if (fallback) setLastFileId(fallback);
      }
    }
  }, [FV.splitscreenFileIds, FV.activeFileId]);

  // Persist current splitscreen configuration whenever it changes (skip during restore)
  useEffect(() => {
    if (restoringRef.current) return;
    try {
      localStorage.setItem(
        "lastSplitscreenFileIds",
        JSON.stringify(FV.splitscreenFileIds || [])
      );
    } catch (e) {
      // ignore
    }
  }, [FV.splitscreenFileIds]);

  // Persist active pane id for restoring selection
  useEffect(() => {
    if (restoringRef.current) return;
    try {
      localStorage.setItem("lastActiveFileId", FV.activeFileId || "");
    } catch (e) {
      // ignore
    }
  }, [FV.activeFileId]);

  // Replace the OLD active pane with the newly selected file when in real split mode.
  // We keep a ref of the previous activeFileId to know which pane to replace.
  useEffect(() => {
    const curr = FV.activeFileId;
    const prev = prevActiveRef.current;

    const hasRealSplit = FV.splitscreenFileIds.length > 1;
    if (!hasRealSplit) {
      prevActiveRef.current = curr;
      return;
    }
    if (!curr) {
      prevActiveRef.current = curr;
      return;
    }
    // If the current active is already in the split, just track and bail
    if (FV.splitscreenFileIds.includes(curr)) {
      prevActiveRef.current = curr;
      return;
    }

    // Determine which pane to replace: the one that was active BEFORE this change
    let targetId = null;
    if (prev && FV.splitscreenFileIds.includes(prev)) targetId = prev;
    // As a fallback, use the first pane
    if (!targetId) targetId = FV.splitscreenFileIds[0] || null;

    if (targetId) {
      const idx = FV.splitscreenFileIds.indexOf(targetId);
      if (idx !== -1) {
        const next = FV.splitscreenFileIds.slice();
        next[idx] = curr;
        FV.setSplitscreenFileIds(next);
        localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(next));
        setLastFileId(curr);
        FV.updateDimensions();
      }
    }

    // Update prev active ref after handling
    prevActiveRef.current = curr;
  }, [FV.activeFileId, FV.splitscreenFileIds]);

  // If user navigates (or reloads) with a URL fileId that does not belong to the current splitscreen,
  // clear the splitscreen and activate the URL file. This keeps the URL as source of truth.
  useEffect(() => {
    if (restoringRef.current) return;
    let urlFileId = null;
    try {
      const params = new URLSearchParams(window.location.search || "");
      urlFileId = params.get("fileId");
    } catch (e) {
      urlFileId = null;
    }
    if (!urlFileId) return;
    const splitLen = (FV.splitscreenFileIds || []).length;
    // In real splitscreen, don't collapse; the replacement effect (above) will swap the active pane.
    if (splitLen > 1) return;
    // In single/empty state, enforce URL file as the single pane
    if (splitLen >= 0 && !FV.splitscreenFileIds.includes(urlFileId)) {
      // Force single view with the URL fileId
      FV.setSplitscreenFileIds([urlFileId]);
      try {
        localStorage.setItem(
          "lastSplitscreenFileIds",
          JSON.stringify([urlFileId])
        );
      } catch (e) {
        /* ignore */
      }
      if (FV.activeFileId !== urlFileId) FV.setActiveFileId(urlFileId);
      setLastFileId(urlFileId);
    }
  }, [FV.splitscreenFileIds, FV.activeFileId]);

  // Used for drag/drop or explicit multi-pane changes only
  const handleFileChange = async (dropTriggered, file, idChange = false) => {
    let fileId = idChange ? file : await fetchFileId(file);
    if (!fileId) return;
    // Only short-circuit if this file is already present; allow re-add when list was cleared
    if (FV.splitscreenFileIds.includes(fileId)) {
      if (lastFileId !== fileId) setLastFileId(fileId);
      if (FV.activeFileId !== fileId) FV.setActiveFileId(fileId);
      return;
    }
    // In drag/drop context, we either add a second pane or replace depending on current count
    if (dropTriggered) {
      // Always APPEND on drop when the file isn't present yet. This enables adding third, fourth, ... panes.
      addToSplitscreen(fileId);
    } else {
      // Non-drop flows should not create additional panes; replace current active pane if present
      changeFile(fileId);
    }
    if (FV.activeFileId !== fileId) FV.setActiveFileId(fileId);
    FV.updateDimensions();
  };

  const changeFile = (newFileId) => {
    const fileIds = FV.splitscreenFileIds.slice();
    // If only one pane (or none), enforce single-pane replacement
    if (fileIds.length <= 1) {
      const next = [newFileId];
      FV.setSplitscreenFileIds(next);
      localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(next));
      if (FV.activeFileId !== newFileId) FV.setActiveFileId(newFileId);
      return;
    }
    // Real splitscreen (>1): replace active pane, or append if not present
    const activeIdx = fileIds.indexOf(FV.activeFileId);
    if (activeIdx !== -1) {
      fileIds[activeIdx] = newFileId;
    } else if (!fileIds.includes(newFileId)) {
      fileIds.push(newFileId);
    }
    FV.setSplitscreenFileIds(fileIds);
    if (FV.activeFileId !== newFileId) FV.setActiveFileId(newFileId);
    localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(fileIds));
  };

  /**
   * Handles the drop event for files in the splitscreen file viewer.
   * Prevents the default browser behavior, retrieves the dragged file's ID,
   * and triggers the file change handler if a valid file ID is found.
   * Also hides the splitscreen dropzone after processing.
   *
   * @param {React.DragEvent<HTMLDivElement>} e - The drag event object.
   */
  const onDropFile = (e) => {
    e.preventDefault();
    const dragFileId = e.dataTransfer.getData("fileId");
    if (!dragFileId) {
      console.warn("No fileId found in drag event.");
      return;
    }
    handleFileChange(true, dragFileId, isGuid(dragFileId));
    setShowSplitscreenDropzone(false);
  };

  const addToSplitscreen = (fileId) => {
    const fileIds = FV.splitscreenFileIds.slice();
    if (!fileIds.includes(fileId)) fileIds.push(fileId);
    FV.setSplitscreenFileIds(fileIds);
    localStorage.setItem("lastSplitscreenFileIds", JSON.stringify(fileIds));
    FV.updateDimensions();
  };

  /**
   * Handles the exclusion of a file from the splitscreen view.
   *
   * Removes the specified file from the splitscreen file IDs, updates the active file ID if necessary,
   * persists the updated splitscreen file IDs to localStorage, updates the viewer dimensions,
   * and stops the event from propagating further.
   *
   * @param {string} fileId - The ID of the file to exclude from the splitscreen.
   * @param {React.SyntheticEvent} e - The event object associated with the exclusion action.
   */
  const onExcludeSplitscreen = (fileId, e) => {
    let splitScreenFileIds = FV.splitscreenFileIds.slice();
    let activeFileId = FV.activeFileId;

    removeRenderer(fileId);
    // Remove fileId from splitScreenFileIds
    splitScreenFileIds = splitScreenFileIds.filter((id) => id !== fileId);
    FV.setSplitscreenFileIds(splitScreenFileIds);

    // if activeFileId not in splitScreenFileIds, set it to the first one
    if (!splitScreenFileIds.includes(activeFileId)) {
      activeFileId =
        splitScreenFileIds.length > 0 ? splitScreenFileIds[0] : null;
      FV.setActiveFileId(activeFileId);
      setLastFileId(activeFileId);
    }

    localStorage.setItem(
      "lastSplitscreenFileIds",
      JSON.stringify(splitScreenFileIds)
    );
    FV.updateDimensions();
    e.stopPropagation();
  };

  const removeRenderer = (fileId) => {
    let dict = FV.rendererDict;
    delete dict[fileId];
    FV.setRendererDict(dict);
  };

  const setActiveView = (currentFileId) => {
    if (FV.activeFileId === currentFileId) return;
    FV.setActiveFileId(currentFileId);
    // Remember which pane is currently active so subsequent selections from the preview list
    // can replace this specific pane in real split-screen mode.
    setLastFileId(currentFileId);
  };

  const fetchFileId = async (file) => {
    let sceneDataList = await Backend.getFileIdData(file);
    if (!sceneDataList || !("sceneIds" in sceneDataList)) return;
    let sceneZero = sceneDataList.sceneIds.find((c) => c.scene === 0);
    if (!sceneZero) return;
    return sceneZero.id;
  };

  const getSplitscreenGridLayout = () => {
    return FV.splitscreenFileIds.length === 4
      ? 6
      : FV.splitscreenFileIds.length > 2
      ? 4
      : FV.splitscreenFileIds.length > 1
      ? 6
      : 12;
  };

  const getSplitscreenGridLayoutHeight = () => {
    return FV.splitscreenFileIds.length === 4
      ? "50%"
      : FV.splitscreenFileIds.length > 6
      ? "33.333333333%"
      : FV.splitscreenFileIds.length > 3
      ? "50%"
      : "100%";
  };

  const openNextFile = () => {
    const idx = FV.sceneDataList.findIndex((obj) => obj.id === FV.activeFileId);
    const nextId =
      idx < FV.sceneDataList.length - 1
        ? FV.sceneDataList[idx + 1].id
        : FV.sceneDataList[FV.sceneDataList.length - 1].id;

    let path = null;
    Object.entries(FV.sceneList).forEach(([key, value]) => {
      if (value.some((obj) => obj.id === nextId)) path = key;
    });

    if (path) {
      FV.setActiveFileId(nextId);
      FV.setActiveFilePath(path);
    }
  };

  const openPrevFile = () => {
    const idx = FV.sceneDataList.findIndex((obj) => obj.id === FV.activeFileId);
    const prevId =
      idx - 1 >= 0 ? FV.sceneDataList[idx - 1].id : FV.sceneDataList[0].id;

    let path = null;
    Object.entries(FV.sceneList).forEach(([key, value]) => {
      if (value.some((obj) => obj.id === prevId)) path = key;
    });

    if (path) {
      FV.setActiveFileId(prevId);
      FV.setActiveFilePath(path);
    }
  };

  const isGuid = (str) => {
    var guidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
  };

  return (
    <div>
      <Grid
        container
        className={classes.rendererGridRoot}
        style={{
          flexBasis: "calc(100% - " + (FV.treeWidth + FV.previewWidth) + "px)",
        }}
        onDragEnter={() => {
          setShowSplitscreenDropzone(true);
        }}
        onDragLeave={() => {
          setShowSplitscreenDropzone(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          onDropFile(e);
        }}
      >
        {showSplitscreenDropzone && (
          <div className={classes.dropfieldRoot}>
            Drop file here for split screen.
          </div>
        )}
        {FV.splitscreenFileIds.map((currentFileId, index) => (
          <Grid
            item
            key={index}
            xs={getSplitscreenGridLayout()}
            className={classes.splitscreenGridRoot}
            style={{
              border: FV.splitscreenFileIds.length > 1 && "1px solid",
              height: getSplitscreenGridLayoutHeight(),
              zIndex: currentFileId === FV.activeFileId ? "0" : "1",
            }}
            onClick={
              FV.splitscreenFileIds.length > 1
                ? () => setActiveView(currentFileId)
                : undefined
            }
          >
            <ImportProgressDialog
              open={
                FV.processingRotation &&
                FV.importProgress > -1 &&
                FV.importProgress <= 100
              }
              processingRotation={FV.processingRotation}
              activeFileName={FV.activeFileName}
              importProgress={FV.importProgress}
              onClose={(e) => e.stopPropagation()}
              onAbort={() => {
                FV.onStopRotationImport(FV.activeFileId);
              }}
              abortLabel={"Stop Rotation"}
            />
            <React.Fragment>
              {currentFileId === FV.activeFileId &&
                FV.splitscreenFileIds.length > 1 && (
                  <div className={classes.activeIndicatorBorder} />
                )}
              {FV.splitscreenFileIds.length > 1 && (
                <div className={classes.layoutButtonContainer}>
                  <Tooltip disableInteractive title={"Chain"}>
                    <IconButton
                      className={classes.attachButton}
                      style={{ color: "#666" }}
                      size="small"
                      onClick={(e) => FV.onChainSplitscreen(currentFileId, e)}
                    >
                      {FV.chainListFileIds.includes(currentFileId) ? (
                        <LinkIcon style={{ color: "#0673C1" }} />
                      ) : (
                        <LinkOffIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove" disableInteractive>
                    <IconButton
                      className={classes.layoutButton}
                      size="small"
                      onClick={(e) => onExcludeSplitscreen(currentFileId, e)}
                    >
                      <RemoveCircleIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              <FileRendererDataProvider
                FV={FV}
                chainListFileIds={FV.chainListFileIds}
                currentFileId={currentFileId}
                index={index}
                getMousePosition={props.getMousePosition}
                updateDimensions={FV.updateDimensions}
                openNextFile={openNextFile}
                openPrevFile={openPrevFile}
              />
            </React.Fragment>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

FileViewerSplitscreenManager.propTypes = {
  persistentStorage: PropTypes.object,
  tiles: PropTypes.object,
  getMousePosition: PropTypes.func,
};

export default withPersistentStorage(withTiles(FileViewerSplitscreenManager));
